import { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { answerPropType } from '@aytm/app/stats/page/question-views/components/lib/prop-types'

import { hexToRgba } from '@aytm/app/stats/lib/render/colors'
import { useComponentWidth } from '@aytm/app/stats/page/question-views/components/charts/lib/hooks'
import { removeLogicFromText } from '@aytm/app/stats/page/question-views/components/charts/highcharts/lib/textUtils'

import {
  computeScores,
  computeRadius
} from '@aytm/app/stats/page/question-views/info-view/components/charts/quadrant/lib/utils'

import QuadrantChart from '@aytm/app/stats/page/question-views/components/charts/highcharts/QuadrantChart'

// First paint, before the container reports its own box. Matches the chart container's own default.
const DEFAULT_WIDTH = 814

const InfoViewQuadrantChart = ({
  questionKey,
  questionId,
  format = {},
  answer,
  batches,
  questionVariants,
  xAxisLimits,
  yAxisLimits,
  colorTheme,
  chartLegendMode,
  features,
  onChartReady
}) => {
  // The quadrant is drawn square, so it needs the number rather than letting the chart fill its
  // box: the height is the width, and the plot radius is derived from it.
  const { componentRef, width } = useComponentWidth({ defaultWidth: DEFAULT_WIDTH })

  const scores = useMemo(() => computeScores(answer, batches), [answer, batches])

  const dataLabels = questionVariants.length > 10 ? 'hover' : 'on'

  const title = format.title || (format.plots ? ' ' : '')

  const calcModelDataFn = useCallback(() => {
    const result = {
      xTitle: format.xTitle || removeLogicFromText(batches[0]?.text) || '',
      yTitle: format.yTitle || removeLogicFromText(batches[1]?.text) || ''
    }

    const r = computeRadius(scores, width, format.size)

    if (answer) {
      result.data = {}
      for (const qv of questionVariants) {
        const k = `qv${qv.position}`
        result.data[k] = {
          color: hexToRgba(colorTheme[(qv.position - 1) % colorTheme.length], 0.9),
          data: [[answer.meanList?.[k]?.['b1'], answer.meanList?.[k]?.['b2'], r]]
        }
      }
    }

    return result
  }, [format, answer, batches, questionVariants, scores, width, colorTheme])

  return (
    <QuadrantChart
      ref={componentRef}
      width={width}
      height={width}
      questionKey={questionKey}
      questionId={questionId}
      calcModelData={calcModelDataFn}
      questionVariants={questionVariants}
      batches={batches}
      scores={scores}
      crossing={format.crossing}
      plots={format.plots}
      title={title}
      dataLabels={dataLabels}
      xAxisLimits={xAxisLimits}
      yAxisLimits={yAxisLimits}
      colorTheme={colorTheme}
      chartLegendMode={chartLegendMode}
      features={features}
      onChartReady={onChartReady}
    />
  )
}

InfoViewQuadrantChart.propTypes = {
  onChartReady: PropTypes.func,
  questionKey: PropTypes.string,
  questionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  format: PropTypes.object,
  answer: answerPropType,
  batches: PropTypes.array.isRequired,
  questionVariants: PropTypes.array.isRequired,
  xAxisLimits: PropTypes.array,
  yAxisLimits: PropTypes.array,
  colorTheme: PropTypes.array.isRequired,
  chartLegendMode: PropTypes.string,
  features: PropTypes.object
}

InfoViewQuadrantChart.displayName = 'InfoViewQuadrantChart'

export default InfoViewQuadrantChart
